// The caster's own reserves: what a self-sourced cast draws from and gives back.
//
// Moved out of game/src/playerStats.ts (2026-07-30, sim extraction step 4b). The
// numbers themselves already lived in WorldState (step 2); what moved here is the
// handful of rules about how they change -- that a draw takes only what's actually
// in reserve, that an Unbound cast's Mana draw can force the shortfall out of
// Health, that a give clamps at the maximum. The cast handlers in casts.ts need those
// rules, and they cannot reach into `game/`.
//
// playerStats.ts still exports the same names with the same signatures; it is now
// a one-line delegator per function, binding each to the game's single world.
import { WorldState } from './state';
import { SimEvents } from './events';

/** Pushes `player-died` the moment Health actually crosses from positive to
 * 0 in this call -- not merely whenever it happens to already read 0, which
 * would re-fire on every further hit taken while already dead. Shared by
 * damagePlayer and forceDrawMana, the only two places Health ever moves,
 * so neither has to duplicate the crossing check. */
function checkDeath(before: number, world: WorldState, out: SimEvents): void {
  if (before > 0 && world.player.health === 0) out.push({ kind: 'player-died' });
}

export function getMana(world: WorldState): number {
  return world.player.mana;
}

export function getHealth(world: WorldState): number {
  return world.player.health;
}

export function getCharge(world: WorldState): number {
  return world.player.charge;
}

/** Draws up to `amount` Mana, taking only what's actually in reserve -- the safe
 * path (Precise/Standard draws): coming up short just weakens the effect, same as an
 * ordinary object source running low. Returns how much was actually drawn. */
export function drawMana(world: WorldState, amount: number): number {
  const player = world.player;
  const actual = Math.min(amount, player.mana);
  player.mana -= actual;
  return actual;
}

/** Forces the full requested amount out regardless of reserve: whatever Mana
 * doesn't cover comes out of Health instead. This is the "attempted an Unbound draw
 * the caster's body couldn't actually cover" danger case, not ordinary use --
 * see 10_Diary.md. Returns the Health cost (0 if Mana alone covered it). */
export function forceDrawMana(world: WorldState, amount: number, out: SimEvents): number {
  const player = world.player;
  const fromReserve = Math.min(amount, player.mana);
  const deficit = amount - fromReserve;
  player.mana -= fromReserve;
  const before = player.health;
  player.health = Math.max(0, player.health - deficit);
  checkDeath(before, world, out);
  return deficit;
}

export function giveMana(world: WorldState, amount: number): void {
  const player = world.player;
  player.mana = Math.min(world.physics.player.maxMana, player.mana + amount);
}

/** Draws up to `amount` Charge, taking only what's actually in reserve.
 * Deliberately has no `forceDrawCharge` counterpart: unlike Energy, there is no
 * ambient bioelectric reserve to force the shortfall out of Health from, so a
 * Charge draw -- even an over-ambitious Unbound one -- always just weakens if the
 * player hasn't charged up enough first, never costs Health. Returns how much
 * was actually drawn. */
export function drawCharge(world: WorldState, amount: number): number {
  const player = world.player;
  const actual = Math.min(amount, player.charge);
  player.charge -= actual;
  return actual;
}

export function giveCharge(world: WorldState, amount: number): void {
  const player = world.player;
  player.charge = Math.min(world.physics.player.maxCharge, player.charge + amount);
}

/** Reduces Health by `amount`, clamped at 0 -- the same clamp forceDrawMana
 * already uses for an Unbound draw's shortfall. Unlike that, this isn't a
 * cast cost: it's incidental danger from the world itself (a live Metal Floor
 * tile, or the caster's own thermalState sitting in a danger band -- see
 * checkThermalDanger below), not something the caster chose to attempt. */
export function damagePlayer(world: WorldState, amount: number, out: SimEvents): void {
  const before = world.player.health;
  world.player.health = Math.max(0, world.player.health - amount);
  checkDeath(before, world, out);
}

export function getThermalState(world: WorldState): number {
  return world.player.thermalState;
}

/** Draws up to `amount` thermalState, taking only what's actually available --
 * conservation, the same shape drawCharge already has. Deliberately has no
 * "force the shortfall out of Health" variant the way Mana's forceDrawMana
 * does: giving away more warmth than the caster has makes no physical sense.
 * Extreme values are dangerous in their own right (see checkThermalDanger),
 * a completely separate mechanism from a forced draw. */
export function drawThermal(world: WorldState, amount: number, out: SimEvents): number {
  const player = world.player;
  const actual = Math.min(amount, player.thermalState);
  player.thermalState -= actual;
  checkThermalDanger(world, out);
  return actual;
}

export function giveThermal(world: WorldState, amount: number, out: SimEvents): void {
  const player = world.player;
  // No ceiling (2026-08-09). `maxThermalState` used to clamp here, which meant
  // heat delivered to an already-hot caster was silently discarded -- and, worse,
  // that being at the top of the scale made you *immune* to further heating. A
  // temperature has no maximum; what has a maximum is the bar drawn for it. The
  // floor at 0 stays, in `drawThermal`: that one is real (absolute zero), and it
  // is a floor on what can be *taken*, not a cap on what can arrive.
  player.thermalState += amount;
  checkThermalDanger(world, out);
}

/** Harm from being outside the survivable band, in either direction --
 * the player-side equivalent of ObjectDef.barrier.meltsWhen's aboveOrBelow
 * shape, applied to the caster specifically. Exported (not local, unlike
 * checkDeath) because solveThermalStep (sim/thermal.ts) mutates
 * world.player.thermalState directly via its own summed-edge math rather than
 * through drawThermal/giveThermal, and still has to funnel through this same
 * check afterward.
 *
 * **Damage is proportional to how far outside the band you are** (2026-08-09),
 * `thermalDamagePerExcess` per point, ramping from nothing exactly at the
 * threshold. It used to be a flat `thermalDamagePerCheck` the instant either
 * threshold was crossed -- a trip switch, which meant a Scorched Floor and a
 * lava flow did identical damage per tick and no amount of extra heat could
 * make anything more dangerous than anything else. The same argument the
 * Charge hazard already answered on 2026-08-08, when a flat `shockDamage`
 * became a bill for the Charge that actually crossed.
 *
 * **Deliberately a function of state, not of flux.** Damage depends on where
 * the caster *is*, not on whether heat is still arriving. The alternative --
 * scaling with the rate of transfer -- collapses the moment the caster
 * equalizes with what they are standing on: a Scorched Floor holds Energy 10
 * and the caster's own scale tops out around there, so they would heat to a
 * lethal reading, reach equilibrium, and then take *nothing* while sitting in
 * it. "It stops hurting once you have finished cooking" is not a rule this
 * world should teach, and it would read even more plainly under a turn-based
 * cadence, where each turn's damage is a discrete number rather than a
 * wall-clock trickle.
 *
 * Kept as two separate branches even though the bodies are mirror images, so a
 * future difference between the two directions (distinct narration, a steeper
 * curve one way) doesn't need restructuring to add. */
export function checkThermalDanger(world: WorldState, out: SimEvents): void {
  const { thermalState } = world.player;
  const { thermalFrozenThreshold, thermalBurningThreshold, thermalDamagePerExcess } = world.physics.player;
  const hurt = (excess: number) => {
    const amount = thermalDamagePerExcess * excess;
    damagePlayer(world, amount, out);
    out.push({ kind: 'stats-changed' });
    out.push({ kind: 'player-hurt', amount });
  };
  if (thermalState < thermalFrozenThreshold) hurt(thermalFrozenThreshold - thermalState);
  else if (thermalState > thermalBurningThreshold) hurt(thermalState - thermalBurningThreshold);
}
